import { useState, useEffect, useContext } from "react";
import { UserContext } from "../../../contexts/user.context";
import {
  Get,
  Post,
  Patch,
  handleError,
  DeleteAndSendData,
} from "../../../utils/fetch.util";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import LoadingData from "../../../components/loadingData/loadingData.component";
import {
  FiPhone,
  FiEdit,
  FiTrash2,
  FiCalendar,
  FiUser,
  FiTruck,
  FiPlus,
  FiX,
} from "react-icons/fi";
import Select from "react-select";
import Image from "../../../components/Image/Image.component";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

interface VisitorRequest {
  id: number;
  name: string;
  reason: string;
  nationalId: string;
  phone: string;
  qrcode: string;
  qrcode_url: string;
  startDate: string;
  endDate: string;
  allowVehicle: boolean;
  current_status: "pending" | "approved" | "rejected";
  createdAt: string;
  updatedAt: string;
  created_by: {
    asdc_client_user_id: number;
    asdc_client_user_name: string;
    profile_pic: string;
  };
}

interface ApiResponse {
  status: string;
  count: number;
  data: VisitorRequest[];
}

export default function MyVisitorRequests() {
  const { t } = useTranslation();
  const { getValidToken } = useContext(UserContext);

  // State management
  const [visitorRequests, setVisitorRequests] = useState<VisitorRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize] = useState(10);
  const [activeTab, setActiveTab] = useState<"visitor" | "vehicle">("visitor");
  const { userData } = useContext(UserContext);
  const sites =
    userData[0]?.user_sites_relationShip?.map((site: any) => site?.site) || [];

  const sitesOptions = sites.map((site: any) => ({
    value: site.site_id,
    label: site.site_name,
  }));

  const defaultSiteId = sites.length === 1 ? sites[0]?.site_id : 0;

  const [siteId, setSiteId] = useState<{ value: number; label: string } | null>(
    null
  );

  // Create visitor request modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    reason: "",
    nationalId: "",
    phone: "",
    startDate: "",
    endDate: "",
    allowVehicle: false,
    allowAllSites: true,
    allowedSiteIds: [] as number[] | undefined,
  });

  // Update visitor request modal state
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<VisitorRequest | null>(
    null
  );
  const [updateFormData, setUpdateFormData] = useState({
    id: 0,
    name: "",
    reason: "",
    nationalId: "",
    phone: "",
    startDate: "",
    endDate: "",
    allowVehicle: false,
    allowedSiteIds: [] as number[],
  });

  // Delete confirmation state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<VisitorRequest | null>(
    null
  );

  // Fetch visitor requests
  const fetchVisitorRequests = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        pageItemsCount: pageSize.toString(),
        vehicle_only: activeTab === "vehicle" ? "true" : "false",
        ...(searchTerm && { search: searchTerm }),
        ...(siteId?.value && { site_id: siteId.value }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate }),
      } as any);

      const response = await Get(
        `visitor-requests/get-my-visitor-requests?${queryParams}`,
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      const apiResponse: VisitorRequest[] = response.data;
      setVisitorRequests(apiResponse);
      setTotalCount(response.count);
    } catch (error) {
      console.error("Error fetching visitor requests:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVisitorRequests();
  }, [currentPage, searchTerm, siteId, startDate, endDate, activeTab]);

  console.log(formData.allowedSiteIds);
  // Create visitor request function
  const createVisitorRequest = async () => {
    setCreateLoading(true);

    const body = {
      ...formData,
    };

    if (body.allowAllSites) {
      delete body.allowedSiteIds;
    }

    body.allowedSiteIds = body.allowedSiteIds?.map((site: any) => site?.value);

    try {
      const response = await Post(
        "visitor-requests/create-visitor-request",
        body,
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      // Reset form and close modal
      setFormData({
        name: "",
        reason: "",
        nationalId: "",
        phone: "",
        startDate: "",
        endDate: "",
        allowVehicle: false,
        allowAllSites: true,
        allowedSiteIds: [] as number[],
      });
      setShowCreateModal(false);

      // Refresh the list
      fetchVisitorRequests();
    } catch (error) {
      console.error("Error creating visitor request:", error);
    } finally {
      setCreateLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate form
  const isFormValid = () => {
    return (
      formData.name.trim() &&
      formData.reason.trim() &&
      formData.nationalId.trim() &&
      formData.phone.trim() &&
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) <= new Date(formData.endDate)
    );
  };

  // Handle update form input changes
  const handleUpdateInputChange = (field: string, value: any) => {
    setUpdateFormData((prev) => ({ ...prev, [field]: value }));
  };

  // Validate update form
  const isUpdateFormValid = () => {
    return (
      updateFormData.name.trim() &&
      updateFormData.reason.trim() &&
      updateFormData.nationalId.trim() &&
      updateFormData.phone.trim() &&
      updateFormData.startDate &&
      updateFormData.endDate &&
      new Date(updateFormData.startDate) <= new Date(updateFormData.endDate)
    );
  };

  // Open update modal
  const openUpdateModal = (request: VisitorRequest) => {
    setSelectedRequest(request);
    setUpdateFormData({
      id: request.id,
      name: request.name,
      reason: request.reason,
      nationalId: request.nationalId,
      phone: request.phone,
      startDate: request.startDate.split("T")[0], // Convert to YYYY-MM-DD format
      endDate: request.endDate.split("T")[0], // Convert to YYYY-MM-DD format
      allowVehicle: request.allowVehicle,
      allowedSiteIds: sites.map((site: any) => site.site_id), // Default to all available sites
    });
    setShowUpdateModal(true);
  };

  // Update visitor request function
  const updateVisitorRequest = async () => {
    setUpdateLoading(true);
    try {
      const response = await Patch(
        "visitor-requests/update-visitor-request",
        updateFormData,
        getValidToken()
      );

      if (response.error || !response.data) {
        handleError(response);
        return;
      }

      // Close modal and refresh list
      setShowUpdateModal(false);
      setSelectedRequest(null);
      fetchVisitorRequests();
    } catch (error) {
      console.error("Error updating visitor request:", error);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Open delete confirmation modal
  const openDeleteModal = (request: VisitorRequest) => {
    setRequestToDelete(request);
    setShowDeleteModal(true);
  };

  // Delete visitor request function
  const deleteVisitorRequest = async () => {
    if (!requestToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await DeleteAndSendData(
        `visitor-requests/delete-visitor-request`,
        { id: requestToDelete.id },
        getValidToken()
      );

      if (response.error) {
        handleError(response);
        return;
      }

      // Close modal and refresh list
      setShowDeleteModal(false);
      setRequestToDelete(null);
      fetchVisitorRequests();
    } catch (error) {
      console.error("Error deleting visitor request:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Status badge component
  const StatusBadge = ({ status }: { status: string }) => {
    const getStatusStyles = () => {
      switch (status.toLowerCase()) {
        case "approved":
          return "bg-green-100 text-green-700 border-green-200";
        case "rejected":
          return "bg-red-100 text-red-700 border-red-200";
        case "pending":
        default:
          return "bg-yellow-100 text-yellow-700 border-yellow-200";
      }
    };

    return (
      <span
        className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusStyles()}`}
      >
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  // Visitor request card component
  const VisitorRequestCard = ({ request }: { request: VisitorRequest }) => (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6 hover:shadow-lg transition-shadow">
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold">
            {request.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {request.name}
            </h3>
            <p className="text-sm text-gray-500">ID: {request.nationalId}</p>
          </div>
        </div>
        <StatusBadge status={request.current_status} />
      </div>

      <div>
        <Image
          src={request?.qrcode_url}
          alt={request.name}
          className="w-[75%] mx-auto"
        />
      </div>

      <div className="space-y-3">
        <div className="flex items-center text-gray-600">
          <FiUser className="w-4 h-4 mr-2" />
          <span className="text-sm">{request.reason}</span>
        </div>

        <div className="flex items-center text-gray-600">
          <FiPhone className="w-4 h-4 mr-2" />
          <span className="text-sm">{request.phone}</span>
        </div>

        <div className="flex items-center text-gray-600">
          <FiCalendar className="w-4 h-4 mr-2" />
          <span className="text-sm">
            {format(new Date(request.startDate), "MMM dd, yyyy")} -{" "}
            {format(new Date(request.endDate), "MMM dd, yyyy")}
          </span>
        </div>

        {request.allowVehicle && (
          <div className="flex items-center text-green-600">
            <FiTruck className="w-4 h-4 mr-2" />
            <span className="text-sm">Vehicle allowed</span>
          </div>
        )}

        <div className="text-xs text-gray-400 mt-3">
          Created: {format(new Date(request.createdAt), "MMM dd, yyyy HH:mm")}
        </div>
      </div>

      <div className="flex space-x-2 mt-4 pt-4 border-t border-gray-100">
        {/* <button className="flex items-center justify-center p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
          <FiPhone className="w-4 h-4" />
        </button> */}
        <button
          onClick={() => openUpdateModal(request)}
          className="flex items-center justify-center p-2 text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
        >
          <FiEdit className="w-4 h-4" />
        </button>
        <button
          onClick={() => openDeleteModal(request)}
          className="flex items-center justify-center p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
        >
          <FiTrash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
  );

  // Pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalCount / pageSize);

    return (
      <div className="flex items-center justify-between mt-6">
        <div className="text-sm text-gray-700">
          Showing {(currentPage - 1) * pageSize + 1} to{" "}
          {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
        </div>
        <div className="flex space-x-2">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t("back")}
          </button>
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const page = i + 1;
            return (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`px-3 py-1 text-sm border rounded-md ${
                  currentPage === page
                    ? "bg-blue-500 text-white border-blue-500"
                    : "border-gray-300 hover:bg-gray-50"
                }`}
              >
                {page}
              </button>
            );
          })}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            {t("next")}
          </button>
        </div>
      </div>
    );
  };

  if (loading) {
    return <LoadingData />;
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            My Visitor Requests
          </h1>
          <p className="text-gray-600">
            Manage and track your visitor requests
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center space-x-2 bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-colors"
        >
          <FiPlus className="w-4 h-4" />
          <span>Create Request</span>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6">
        <button
          onClick={() => setActiveTab("visitor")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
            activeTab === "visitor"
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Visitor
        </button>
        <button
          onClick={() => setActiveTab("vehicle")}
          className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
            activeTab === "vehicle"
              ? "bg-blue-500 text-white"
              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
          }`}
        >
          Vehicle
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              placeholder="Search by visitor name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Site Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select a Site
            </label>
            <Select
              value={siteId}
              onChange={(e) => {
                setSiteId(e);
              }}
              options={sitesOptions}
              placeholder="Select site"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      {visitorRequests?.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-lg mb-2">
            No visitor requests found
          </div>
          <p className="text-gray-500">Try adjusting your search criteria</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {visitorRequests?.map((request) => (
              <VisitorRequestCard key={request.id} request={request} />
            ))}
          </div>
          <Pagination />
        </>
      )}

      {/* Create Visitor Request Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Create Visitor Request
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                createVisitorRequest();
              }}
              className="space-y-4"
            >
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visitor Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange("name", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter visitor name"
                  required
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Visit *
                </label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={(e) => handleInputChange("reason", e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Business meeting"
                  required
                />
              </div>

              {/* National ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  National ID *
                </label>
                <input
                  type="text"
                  maxLength={18}
                  value={formData.nationalId}
                  onChange={(e) => {
                    if (
                      e.target.value.length <= 18 &&
                      Number(e.target.value) >= 0
                    ) {
                      handleInputChange("nationalId", e.target.value);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter national ID"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <PhoneInput
                  country="eg"
                  value={formData.phone}
                  inputProps={{
                    style: { width: "100%" },
                    // maxLength: 15,
                  }}
                  inputStyle={{ height: "40px" }}
                  onChange={(phone: any) => {
                    handleInputChange("phone", "+" + phone);
                  }}
                  enableSearch={true}
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.startDate}
                    onChange={(e) =>
                      handleInputChange("startDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.endDate}
                    onChange={(e) =>
                      handleInputChange("endDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Allow All Sites */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allowAllSites"
                  checked={formData.allowAllSites}
                  onChange={(e) =>
                    handleInputChange("allowAllSites", e.target.checked)
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />

                <label
                  htmlFor="allowAllSites"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Allow all sites
                </label>
              </div>

              {!formData.allowAllSites && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Select Sites *
                  </label>
                  <Select
                    value={formData.allowedSiteIds}
                    onChange={(e) => {
                      handleInputChange("allowedSiteIds", e);
                    }}
                    options={sitesOptions}
                    placeholder="Select sites"
                    isMulti
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              )}

              {/* Allow Vehicle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allowVehicle"
                  checked={formData.allowVehicle}
                  onChange={(e) =>
                    handleInputChange("allowVehicle", e.target.checked)
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="allowVehicle"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Allow vehicle access
                </label>
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid() || createLoading}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {createLoading ? "Creating..." : "Create Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Update Visitor Request Modal */}
      {showUpdateModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Update Visitor Request
              </h2>
              <button
                onClick={() => setShowUpdateModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateVisitorRequest();
              }}
              className="space-y-4"
            >
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Visitor Name *
                </label>
                <input
                  type="text"
                  value={updateFormData.name}
                  onChange={(e) =>
                    handleUpdateInputChange("name", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter visitor name"
                  required
                />
              </div>

              {/* Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for Visit *
                </label>
                <input
                  type="text"
                  value={updateFormData.reason}
                  onChange={(e) =>
                    handleUpdateInputChange("reason", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., Business meeting"
                  required
                />
              </div>

              {/* National ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  National ID *
                </label>
                <input
                  type="text"
                  value={updateFormData.nationalId}
                  onChange={(e) =>
                    handleUpdateInputChange("nationalId", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter national ID"
                  required
                />
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number *
                </label>
                <input
                  type="tel"
                  value={updateFormData.phone}
                  onChange={(e) =>
                    handleUpdateInputChange("phone", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., +1234567890"
                  required
                />
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={updateFormData.startDate}
                    onChange={(e) =>
                      handleUpdateInputChange("startDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={updateFormData.endDate}
                    onChange={(e) =>
                      handleUpdateInputChange("endDate", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Allow Vehicle */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="updateAllowVehicle"
                  checked={updateFormData.allowVehicle}
                  onChange={(e) =>
                    handleUpdateInputChange("allowVehicle", e.target.checked)
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label
                  htmlFor="updateAllowVehicle"
                  className="ml-2 block text-sm text-gray-900"
                >
                  Allow vehicle access
                </label>
              </div>

              {/* Allowed Sites */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Allowed Sites
                </label>
                <Select
                  isMulti
                  options={sitesOptions}
                  value={sitesOptions.filter((option: any) =>
                    updateFormData.allowedSiteIds.includes(option.value)
                  )}
                  onChange={(selectedOptions) => {
                    const siteIds = selectedOptions
                      ? selectedOptions.map((option: any) => option.value)
                      : [];
                    handleUpdateInputChange("allowedSiteIds", siteIds);
                  }}
                  className="w-full"
                  placeholder="Select allowed sites"
                />
              </div>

              {/* Form Actions */}
              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowUpdateModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!isUpdateFormValid() || updateLoading}
                  className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {updateLoading ? "Updating..." : "Update Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && requestToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Delete Visitor Request
              </h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete the visitor request for{" "}
                <strong>{requestToDelete.name}</strong>?
              </p>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-700">
                  <strong>Visitor:</strong> {requestToDelete.name}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Reason:</strong> {requestToDelete.reason}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Date:</strong>{" "}
                  {format(new Date(requestToDelete.startDate), "MMM dd, yyyy")}{" "}
                  - {format(new Date(requestToDelete.endDate), "MMM dd, yyyy")}
                </p>
              </div>
              <p className="text-sm text-red-600 mt-3">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={deleteVisitorRequest}
                disabled={deleteLoading}
                className="flex-1 px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {deleteLoading ? "Deleting..." : "Delete Request"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
